import moment from "moment-timezone";
import React from "react";
import { useTranslation } from "react-i18next";

import { formatCurrency } from "../../../../../util";
import { atStation } from "../../../../../util/stationTime";
import {
  CancellationRule,
  cancellationFee,
  cancellationQuote,
} from "../../../../../util/cancellationRule";

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface CancellationTermsProps {
  /** The rule in force for this category (`resolveCancellationRule`); undefined = no rule. */
  rule: CancellationRule | undefined;
  /** Covered by the customer's organisation: free until the start, whatever the rule. */
  covered: boolean;
  /** The selected start (the quote window's `fromDate`), or null while none is chosen. */
  start: string | null;
  /** The total shown above, or null while the quote has not arrived. */
  price: number | null;
  /** `isFreeOfCharge`: a free booking shows no € amounts and no fee percentages. */
  free: boolean;
  /**
   * The offer is CONFIGURED free (`resolveCharging` = `free`). Only then does
   * the server know the price is 0 and let a free booking cancel free until
   * the start (`free_of_charge`); a booking that is free for another reason
   * (a 100 % voucher) reads the AGB sentence instead.
   */
  configuredFree: boolean;
  /** The booked location's zone — every date is its wall clock. */
  timeZone: string;
  /**
   * Until when the booking is only an OFFER — nothing paid, no contract —
   * and withdrawing it is free (PO, 2026-10-19): a pay-first reservation
   * until `payment`, a postpay request until `acceptance`. Null for a direct
   * booking, which is paid in the flow. The rule's tiers bind only after it.
   */
  offerUntil: "payment" | "acceptance" | null;
  /** When the customer looks — defaults to now; a parameter so it is testable. */
  now?: moment.MomentInput;
}

const key = (name: string) => `book_location.booking_summary.cancellation.${name}`;

/** "50 % des Preises (225,00 €)", or without the amount while none is known. */
const feeText = (
  t: Translate,
  language: string,
  percent: number,
  price: number | null
): string =>
  price !== null && price > 0
    ? t(key("percent_amount"), {
        percent,
        amount: formatCurrency(cancellationFee(price, percent), language),
      })
    : t(key("percent"), { percent });

/** No start chosen yet: the rule in days, not dates. */
const linesInDays = (
  t: Translate,
  tiers: CancellationRule["tiers"],
  fee: (percent: number) => string
): string[] => [
  t(key("free_until_days"), { count: tiers[0]?.daysBefore ?? 0 }),
  ...tiers.map((tier) =>
    tier.daysBefore === 0
      ? t(key("tier_start"), { fee: fee(tier.percent) })
      : t(key("tier_days"), { count: tier.daysBefore, fee: fee(tier.percent) })
  ),
];

/**
 * A chosen start: concrete dates. Booked inside the fee window (the free
 * deadline already passed), there is no free line — the block starts with the
 * tier in force now, then the ones still ahead (review of #3394).
 */
const linesInDates = (
  t: Translate,
  rule: CancellationRule,
  start: string,
  now: moment.MomentInput,
  timeZone: string,
  fee: (percent: number) => string
): string[] => {
  const format = t(key("date_format"));
  // A numeric pattern per language (`date_format`), on the location's clock.
  const at = (instant: moment.Moment) => atStation(instant, timeZone).format(format);
  const quote = cancellationQuote(rule, start, now, timeZone);
  const nowMs = moment(now).valueOf();
  if (quote.freeUntil.valueOf() > nowMs) {
    return [
      t(key("free_until"), { date: at(quote.freeUntil) }),
      ...quote.tiers.map((tier) => t(key("tier"), { date: at(tier.from), fee: fee(tier.percent) })),
    ];
  }
  const ahead = quote.tiers.filter((tier) => tier.from.valueOf() > nowMs);
  return [
    t(key("tier_now"), { fee: fee(quote.feePercent) }),
    ...ahead.map((tier) => t(key("tier"), { date: at(tier.from), fee: fee(tier.percent) })),
  ];
};

/** What the rule says once it binds, as sentences. */
const ruleLines = (
  t: Translate,
  language: string,
  { rule, start, price, timeZone, now }: CancellationTermsProps
): string[] => {
  if (!rule) return [t(key("agb"))];
  if (rule.tiers.length === 0) return [t(key("free_until_start"))];
  const fee = (percent: number) => feeText(t, language, percent, price);
  const sorted = [...rule.tiers].sort((a, b) => b.daysBefore - a.daysBefore);
  return start
    ? linesInDates(t, rule, start, now ?? moment(), timeZone, fee)
    : linesInDays(t, sorted, fee);
};

/**
 * The lines of the block, as sentences — pure, so it is testable without
 * rendering. With an offer stage, the first line is the free withdrawal and
 * the second says from when the rule binds; the tiers follow.
 */
export const cancellationTermsLines = (
  t: Translate,
  language: string,
  props: CancellationTermsProps
): string[] => {
  const { covered, free, configuredFree, offerUntil } = props;
  // The organisation's agreement governs a covered booking, not the AGB.
  if (covered) return [t(key("covered"))];
  // A free booking shows no price apparatus (CLAUDE.md): "free until the
  // start" where the server agrees, else the AGB sentence.
  if (free) return [t(key(props.rule && configuredFree ? "free_until_start" : "agb"))];
  const [first = "", ...tiers] = ruleLines(t, language, props);
  if (!offerUntil) return [first, ...tiers];
  return [
    t(key(`withdraw_until_${offerUntil}`)),
    t(key(`binds_after_${offerUntil}`), { terms: first }),
    ...tiers,
  ];
};

/** How many lines lead as paragraphs before the tier list. */
const leadLines = (props: CancellationTermsProps): number =>
  props.offerUntil && !props.covered && !props.free ? 2 : 1;

/**
 * "Stornierung" — what cancelling this booking will cost, shown BEFORE the
 * customer confirms (M10, completion plan §0.3 D5). It states the terms; the
 * server decides at cancel time whether the app may cancel by itself
 * (`bookerCancelVerdict`, ADR-0020).
 *
 * A reservation is an offer until it is paid (pay-first) or accepted
 * (postpay): withdrawing it is free, and the block says so first, then from
 * when the rule binds. A rule shows the free-until date and time and each tier
 * with its amount; no rule the AGB sentence — never "free", because without
 * a rule nothing is claimed either way (PO decision 6). Every rule sentence
 * names "gemäß unseren AGB" / "according to our terms"; a covered booking is
 * free until the start under the organisation's agreement instead, so its
 * sentence names that agreement and not the AGB.
 */
const CancellationTermsBlock: React.FC<CancellationTermsProps> = (props) => {
  const { t, i18n } = useTranslation();
  const lines = cancellationTermsLines(t, i18n.language, props);
  const lead = lines.slice(0, leadLines(props));
  const tiers = lines.slice(leadLines(props));
  return (
    <div className="p-2 mt-2 border border-foreground/10 rounded-md text-sm">
      <p className="mb-2 font-bold">{t("book_location.booking_summary.cancellation.title")}</p>
      {lead.map((line) => (
        <p key={line}>{line}</p>
      ))}
      {tiers.length > 0 && (
        <ul className="mt-1 space-y-1 text-foreground/80">
          {tiers.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CancellationTermsBlock;
